import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { message } from 'antd';
import { EditableTextView, SpinnerView } from 'components';
import ButtonViewV3 from 'components/ButtonViewV3/ButtonViewV3';
import PageHeader, { IPageAction } from 'components/PageHeader/PageHeader';
import PageWrapper from 'components/PageWrapper';
import useChannelDetails, { channelsMap } from 'hooks/useChannelDetails';
import useFetchContentsTemplate from 'hooks/useFetchContentsTemplate';
import usePageTracking from 'hooks/usePageTracking';
import {
  CREATE_CONTENT,
  DELETE_CONTENT,
  EDIT_CONTENT,
  PUBLISH_CONTENT,
  TEST_CONTENT,
} from 'rbac/constants';
import {
  addContentName,
  addEmptyContentToTemplate,
  postTemplateCreation,
  resetContent,
  selectShouldEnableAddContentButton,
  selectShouldEnableMoveToContentButton,
} from 'redux/content/content.slice';
import { RootState } from 'redux/store/store';

import CategoriesTemplate from './CategoriesTemplate';
import { findVariables } from './ContentEditor';
import ContentTemplateWrapper from './ContentTemplateWrapper';
import DiscardModel from './DiscardModel';
import EmptyDataCheckModel from './EmptyDataCheckModel';
import LeftContainer from './LeftContainer';
import RightPanel from './RightPanel';
import TestTemplate from './TestTemplate';

export enum ContentStatus {
  DRAFT = 'draft',
  PUBLISHED = 'published',
  ACTIVE = 'active',
  USED = 'used',
}

const ContentTemplate = () => {
  usePageTracking();
  const [step, setStep] = useState(1);
  const [drawerCollapsed, setDrawerCollapsed] = useState(false);
  const [discardModal, setDiscardModal] = useState(false);
  const [ivrEmptyCheck, setIvrEmptyCheck] = useState(false);
  const dispatch = useDispatch();
  const contentName = useSelector(
    (state: RootState) => state.content.content.name
  );
  const shouldEnableMoveToContentButton = useSelector(
    selectShouldEnableMoveToContentButton
  );
  const templateContents = useSelector(
    (state: RootState) => state.content.content.details
  );
  const nav = useNavigate();
  const content = useSelector((state: RootState) => state.content.content);
  const { channel } = useChannelDetails();

  useEffect(() => {
    if (!contentName) {
      dispatch(
        addContentName({
          contentName:
            channelsMap[channel as keyof typeof channelsMap].templateName,
        })
      );
    }
  }, []);
  function findExtraPatterns(text: string, givenPatterns: string[]) {
    const extractedPatterns = findVariables(text);

    const extraPatterns: string[] = [];
    givenPatterns = givenPatterns.map((pattern) => '{@' + pattern + '}');

    extractedPatterns.forEach((pattern) => {
      if (!givenPatterns.includes(pattern)) {
        extraPatterns.push(pattern);
      }
    });
    return {
      extractedPatterns,
      extraPatterns,
    };
  }

  useEffect(() => {
    return () => {
      dispatch(resetContent());
    };
  }, []);

  useEffect(() => {
    if (templateContents.length) {
      setStep(2);
    }
  }, [templateContents]);

  useFetchContentsTemplate();
  const filterData = useSelector(
    (store: RootState) => store.content.config.data.Var
  );

  const onPublishHandler = ({ status }: { status: ContentStatus }) => {
    if (contentName) {
      const contentsDetails = content.details?.map((contentItem) => {
        const content =
          channel === 'ivr' ? contentItem.rawText : contentItem.message;
        const { extractedPatterns, extraPatterns } = findExtraPatterns(
          content || '',
          filterData.map((item) => item.fieldName)
        );

        return {
          ...contentItem,
          status,
          extraPatterns,
          variableMapping: extractedPatterns,
        };
      });

      const hasNoExtraPatterns = contentsDetails.every(
        (item) => !item.extraPatterns || item.extraPatterns.length <= 0
      );

      if (hasNoExtraPatterns) {
        const contentsDetailsWithoutExtraPatterns = contentsDetails.map(
          ({ extraPatterns, ...rest }) => rest
        );

        const postTemplateCreationCallBack = () => {
          if (status === ContentStatus.PUBLISHED) {
            nav(`/content/channels/${channel}/overview`);
          }
        };
        dispatch(
          postTemplateCreation({
            content: {
              ...content,
              details: contentsDetailsWithoutExtraPatterns,
            },
            postTemplateCreationCallBack,
            channel,
            status,
          })
        );
      } else {
        const extraPatternsMessage = contentsDetails
          .map((item) => item.extraPatterns.join(', ') + ` in ${item.language}`)
          .join(', ');

        const displayText = {
          [ContentStatus.DRAFT]: 'saving as draft',
          [ContentStatus.PUBLISHED]: 'publishing',
        };
        message.error(
          `Please remove the extra patterns from the template ${extraPatternsMessage} before ${
            displayText[status as keyof typeof displayText]
          }.`
        );
      }
    }
  };

  const shouldEnableAddContentButton = useSelector(
    selectShouldEnableAddContentButton(channel)
  );

  const shouldEnableSaveButtons =
    contentName &&
    shouldEnableAddContentButton &&
    shouldEnableMoveToContentButton;

  const pageActions: IPageAction[] = [
    {
      name: 'Cancel',
      onClick: () => {
        setDiscardModal(true);
      },
      variant: 'tertiary',
      permission: [
        CREATE_CONTENT,
        EDIT_CONTENT,
        PUBLISH_CONTENT,
        DELETE_CONTENT,
        TEST_CONTENT,
      ],
    },
    {
      name: 'Save as Draft',
      permission: [CREATE_CONTENT, EDIT_CONTENT],
      disabled: !(shouldEnableSaveButtons && templateContents.length),
      onClick: () => {
        onPublishHandler({ status: ContentStatus.DRAFT });
      },
      variant: 'tertiary',
    },
    {
      name: 'Publish',
      permission: [PUBLISH_CONTENT],
      disabled: !(shouldEnableSaveButtons && templateContents.length),
      onClick: () => {
        if (
          channel === 'ivr' &&
          content.details.some((item) => !item.rawText)
        ) {
          setIvrEmptyCheck(true);
          return;
        }
        onPublishHandler({ status: ContentStatus.PUBLISHED });
      },
      variant: 'primary',
    },
  ];
  const isLoading = useSelector((state: RootState) => state.content.isLoading);

  return (
    <SpinnerView spinning={isLoading}>
      <PageWrapper>
        <div className="pr-[104px]">
          <PageHeader
            title={
              <div className="flex flex-row gap-3">
                <EditableTextView
                  initialValue={contentName}
                  weight="700"
                  size={18}
                  lineHeight={28}
                  color="#101828"
                  onChange={(text) => {
                    if (text.length < 32) {
                      dispatch(
                        addContentName({
                          contentName: text,
                        })
                      );
                    } else {
                      message.error(
                        'Content name should be less than 32 characters'
                      );
                    }
                  }}
                />
              </div>
            }
            actions={pageActions}
          ></PageHeader>
        </div>
        <div className="flex flex-row w-full">
          <LeftContainer>
            <div style={{ height: '100%' }}>
              {step === 1 && <CategoriesTemplate />}
              {step === 2 && <ContentTemplateWrapper />}
            </div>
            <div
              className={`flex justify-${
                step === 1 ? 'end' : 'start'
              } py-2   pl-[103px]`}
              style={{
                boxShadow: '-4px 0px 4px 0px rgba(0, 0, 0, 0.15)',
                marginRight: '-24px',
                marginLeft: '-104px',
                paddingRight: '2rem',
              }}
            >
              {step === 1 && (
                <div className="flex w-fit ">
                  <ButtonViewV3
                    disabled={!shouldEnableMoveToContentButton}
                    onClick={() => {
                      setStep(2);
                      if (!templateContents.length) {
                        dispatch(addEmptyContentToTemplate());
                      }
                    }}
                    label="Move to content"
                    variant="primary"
                  />
                </div>
              )}
              {step === 2 && (
                <div className="flex w-fit ">
                  <ButtonViewV3
                    onClick={() => {
                      setStep(1);
                    }}
                    label="Back to categories"
                    variant="tertiary"
                  />
                </div>
              )}
            </div>
          </LeftContainer>
          <RightPanel />
        </div>
        <TestTemplate open={drawerCollapsed} onClose={setDrawerCollapsed} />
        <DiscardModel
          open={discardModal}
          onClose={setDiscardModal}
          content={content}
        />
        <EmptyDataCheckModel
          onPublishHandler={onPublishHandler}
          open={ivrEmptyCheck}
          onClose={setIvrEmptyCheck}
        />
      </PageWrapper>
    </SpinnerView>
  );
};

export default ContentTemplate;
